import { z } from 'zod';

import { rhfOptionalIntNumber, rhfOptionalNumber } from './base.js';
import { OptionalTimestampSchema } from './common.js';

// ============================================================================
// Shadow Map Provenance — Phase 9 (The Shadow State)
// Tracks whether each nutrition field was set by AI or overridden by the user,
// preserving the AI's suggested value for XAI tooltips.
// V4.0: Added 'json-ld' source for import provenance tracking.
// ============================================================================
export const ProvenanceEntrySchema = z.object({
    source: z.enum(['user', 'ai', 'web']),
    aiShadowValue: z.union([z.number(), z.string(), z.null()]).optional(),
});

export type ProvenanceEntry = z.infer<typeof ProvenanceEntrySchema>;

// Nutrition Schema for Database - Dual Nutrition Support (for API/DB)
export const RecipeNutritionSchema = z.object({
    recipeId: z.string().uuid().optional(),
    // Per Serving Values
    calories: z.number().int().min(0).optional(),
    proteinG: z.number().min(0).optional(),
    carbsG: z.number().min(0).optional(),
    fatG: z.number().min(0).optional(),
    fiberG: z.number().min(0).optional(),
    sugarG: z.number().min(0).optional(),
    sodiumMg: z.number().int().min(0).optional(),
    // Total Batch Values
    totalCalories: z.number().int().min(0).optional(),
    totalProteinG: z.number().min(0).optional(),
    totalCarbsG: z.number().min(0).optional(),
    totalFatG: z.number().min(0).optional(),
    totalFiberG: z.number().min(0).optional(),
    totalSugarG: z.number().min(0).optional(),
    totalSodiumMg: z.number().int().min(0).optional(),
    // Metadata
    isUserOverridden: z.boolean().optional(),
    dataSource: z.string().optional(),
    provenance: z.record(z.string(), ProvenanceEntrySchema).optional(),
    createdAt: OptionalTimestampSchema,
    updatedAt: OptionalTimestampSchema,
});

// Nutrition Schema for Form Inputs (accepts strings) - ONLY TOTAL VALUES
export const RecipeNutritionFormSchema = z
    .object({
        recipeId: z.string().uuid().optional(),
        // Total Batch Values - accept strings, output numbers
        totalCalories: rhfOptionalIntNumber.optional(),
        totalProteinG: rhfOptionalNumber.optional(),
        totalCarbsG: rhfOptionalNumber.optional(),
        totalFatG: rhfOptionalNumber.optional(),
        totalFiberG: rhfOptionalNumber.optional(),
        totalSugarG: rhfOptionalNumber.optional(),
        totalSodiumMg: rhfOptionalIntNumber.optional(),
        // Metadata
        isUserOverridden: z.boolean().optional(),
        dataSource: z.string().optional(),
        provenance: z.record(z.string(), ProvenanceEntrySchema).optional(),
        createdAt: OptionalTimestampSchema,
        updatedAt: OptionalTimestampSchema,
    })
    .optional()
    .nullable();

export type RecipeNutrition = z.infer<typeof RecipeNutritionSchema>;
export type RecipeNutritionForm = z.infer<typeof RecipeNutritionFormSchema>;